import type { Job, Company, Deal, Contact, Branch, Grower, AuthUser } from "../../../../api/types";
import type { Feature, FeatureCollection, Geometry, Polygon, MultiPolygon } from "geojson";
import type { GeoJSONDataMap, GroupedGeoJSONFiles } from "../../../../utilities/types";
import type { PropertyMappings } from "../../GeoData/PropertyMappingSelector";
import type { FileValidationWarning } from "../../GeoData/GeoTypeSelectorGroup/GeoTypeSelectorGroup.helpers";

// ============================================================================
// Wizard Step Types
// ============================================================================

/**
 * Configuration phase steps (before job creation)
 */
export type ConfigurationStep = "file-validation" | "property-mapping" | "geo-type-selection";

/**
 * Validation phase steps (after job creation)
 */
export type ValidationStep =
	| "duplicate-features"
	| "overlapping-boundaries"
	| "unmatched-features"
	| "missing-invalid-data"
	| "summary";

/**
 * All wizard steps - configuration followed by validation
 */
export type WizardStep = ConfigurationStep | ValidationStep;

/**
 * Wizard phase - configuration (before jobs) or validation (after jobs)
 */
export type WizardPhase = "configuration" | "validation";

/**
 * Configuration phase steps in order
 */
export const CONFIGURATION_STEPS: ConfigurationStep[] = ["file-validation", "property-mapping", "geo-type-selection"];

/**
 * Validation phase steps in order (duplicates first to avoid false positive overlaps)
 */
export const VALIDATION_STEPS: ValidationStep[] = [
	"duplicate-features",
	"overlapping-boundaries",
	"unmatched-features",
	"missing-invalid-data",
	"summary",
];

/**
 * All wizard steps in order
 */
export const WIZARD_STEPS: WizardStep[] = [...CONFIGURATION_STEPS, ...VALIDATION_STEPS];

export const WIZARD_STEP_TITLES: Record<WizardStep, string> = {
	// Configuration steps
	"file-validation": "File Validation",
	"property-mapping": "Property Mapping",
	"geo-type-selection": "Geo-Type Selection",
	// Validation steps
	"overlapping-boundaries": "Overlapping Boundaries",
	"duplicate-features": "Duplicates",
	"unmatched-features": "Unmatched Points/Zones",
	"missing-invalid-data": "Missing/Invalid Data",
	summary: "Summary",
};

export const WIZARD_STEP_DESCRIPTIONS: Record<WizardStep, string> = {
	// Configuration steps
	"file-validation": "Review file parsing warnings and uploaded files",
	"property-mapping": "Configure which file properties map to job fields",
	"geo-type-selection": "Assign boundary, zone, or point types to each file group",
	// Validation steps
	"overlapping-boundaries": "Fix jobs with boundaries that overlap each other",
	"duplicate-features": "Fix jobs with duplicate geospatial features",
	"unmatched-features": "Fix jobs with points/zones that have no boundary",
	"missing-invalid-data": "Fix jobs with missing or invalid property values",
	summary: "Review and load jobs into the import table",
};

// ============================================================================
// Issue Types
// ============================================================================

/**
 * Issue when 2+ jobs have boundaries that geospatially intersect
 */
export interface OverlapIssue {
	id: string;
	jobIds: string[];
	jobs: Job[];
	/** Intersection area in square meters (for display) */
	intersectionArea: number;
	/** The intersecting geometry for map visualization */
	intersectionGeometry?: Feature<Polygon | MultiPolygon>;
	/** Source file paths per job (jobId -> array of file paths) */
	jobFilePaths: Record<string, string[]>;
}

/**
 * Issue when the same geo-features appear in multiple jobs
 */
export interface DuplicateIssue {
	id: string;
	jobIds: string[];
	jobs: Job[];
	/** Type of feature that is duplicated: boundary (field outline), polygon (zone), or point */
	featureType: "boundary" | "polygon" | "point";
	/** Number of duplicate features */
	duplicateCount: number;
	/** Source file paths */
	filePaths: string[];
	/** Source file paths per job (jobId -> array of file paths) */
	jobFilePaths: Record<string, string[]>;
	/** The duplicate features for map visualization */
	duplicateFeatures: Feature<Geometry>[];
}

/**
 * Issue when a job has points/zones but no boundary
 */
export interface UnmatchedIssue {
	id: string;
	job: Job;
	/** The unmatched features (points or zones) */
	unmatchedFeatures: FeatureCollection;
	/** Source file path */
	filePath: string;
	/** Jobs with boundaries within 1 mile */
	nearbyJobs: NearbyJobInfo[];
}

export interface NearbyJobInfo {
	job: Job;
	/** Distance in miles from unmatched features to this job's boundary */
	distanceMiles: number;
}

/**
 * Issue when a job has validation errors on its properties
 */
export interface InvalidDataIssue {
	id: string;
	job: Job;
	/** Map of field name to error message */
	errors: Record<string, string>;
}

// ============================================================================
// Configuration State Types (Phase 1)
// ============================================================================

/**
 * Information about an uploaded file
 */
export interface UploadedFileInfo {
	/** File name */
	name: string;
	/** File path (may include folder structure from ZIP) */
	path: string;
	/** File size in bytes */
	size: number;
}

/**
 * Geo-type that can be assigned to a geodata group
 */
export type GeoType = "Boundary" | "Zones" | "Centroids" | "Cores";

/**
 * Geometry type of a geodata group
 */
export type GeometryType = "polygon" | "point";

/**
 * A grouped collection of geodata from files
 */
export interface GeodataGroup {
	/** Unique identifier for the group */
	id: string;
	/** Display name for the group (auto-generated or from file path) */
	displayName: string;
	/** File paths that make up this group */
	filePaths: string[];
	/** Geometry type of features in this group */
	geometryType: GeometryType;
	/** Number of features in this group */
	featureCount: number;
	/** Property names available in this group's features */
	propertyNames: string[];
	/** The GeoJSON data for this group */
	geoJSON: FeatureCollection;
}

/**
 * Configuration phase state (before job creation)
 */
export interface WizardConfigurationState {
	/** File validation warnings from parsing */
	warnings: FileValidationWarning[];
	/** List of uploaded files */
	uploadedFiles: UploadedFileInfo[];
	/** Grouped geodata from parsing */
	geodataGroups: GeodataGroup[];
	/** Property mappings per group (groupId -> mappings) */
	propertyMappings: Record<string, PropertyMappings>;
	/** Geo-type selections per group (groupId -> type or null) */
	geoTypeSelections: Record<string, GeoType | null>;
	/** Groups that have been excluded from processing */
	excludedGroupIds: Set<string>;
	/** Groups currently selected for map preview */
	selectedGroupIdsForMap: string[];
	/** Original GeoJSON data map from file parsing */
	geoDataMap: GeoJSONDataMap;
	/** Selectable geodata (categorized by geo-type) */
	selectableGeoData: GroupedGeoJSONFiles;
	/** Original files that were uploaded */
	originalFiles: File[];
}

// ============================================================================
// Wizard State
// ============================================================================

export interface WizardIssues {
	overlaps: OverlapIssue[];
	duplicates: DuplicateIssue[];
	unmatched: UnmatchedIssue[];
	invalidData: InvalidDataIssue[];
}

export interface WizardState {
	/** Current wizard phase - configuration (before jobs) or validation (after jobs) */
	phase: WizardPhase;
	/** Configuration phase state (Phase 1 - before job creation) */
	configuration: WizardConfigurationState;
	/** Current array of jobs being processed (Phase 2 - validation) */
	jobs: Job[];
	/** Current wizard step */
	currentStep: WizardStep;
	/** Current issue index within a multi-page step (0-based) */
	currentIssueIndex: number;
	/** All detected issues */
	issues: WizardIssues;
	/** IDs of issues that have been skipped by the user */
	skippedIssueIds: Set<string>;
	/** Steps that have no issues */
	emptySteps: Set<WizardStep>;
	/** Steps that have been completed */
	completedSteps: Set<WizardStep>;
	/** Steps that initially had issues when wizard first ran validation (for progress display) */
	stepsWithInitialIssues: Set<WizardStep>;
	/** Whether validation is currently running */
	isValidating: boolean;
	/** Error message if validation failed */
	validationError: string | null;
	/** If true, the next SET_ISSUES should preserve the current step */
	preserveStepOnNextValidation?: boolean;
}

// ============================================================================
// Wizard Actions
// ============================================================================

export type WizardAction =
	// Configuration phase actions (Phase 1)
	| { type: "SET_CONFIGURATION"; configuration: WizardConfigurationState }
	| { type: "UPDATE_GEODATA"; geodata: Partial<WizardConfigurationState> }
	| { type: "CLEAR_GEODATA" }
	| { type: "UPDATE_PROPERTY_MAPPINGS"; groupId: string; mappings: PropertyMappings }
	| { type: "UPDATE_ALL_PROPERTY_MAPPINGS"; mappings: Record<string, PropertyMappings> }
	| { type: "UPDATE_GEO_TYPE"; groupId: string; geoType: GeoType | null }
	| { type: "UPDATE_ALL_GEO_TYPES"; updates: Record<string, GeoType | null> }
	| { type: "EXCLUDE_GROUP"; groupId: string }
	| { type: "INCLUDE_GROUP"; groupId: string }
	| { type: "SET_SELECTED_GROUPS_FOR_MAP"; groupIds: string[] }
	| { type: "COMPLETE_CONFIGURATION"; jobs: Job[] }
	// Validation phase actions (Phase 2)
	| { type: "SET_JOBS"; jobs: Job[]; preserveStep?: boolean }
	| { type: "UPDATE_JOB"; job: Job }
	| { type: "DELETE_JOB"; jobId: string }
	| { type: "DELETE_JOBS"; jobIds: string[] }
	| { type: "SKIP_ISSUE"; issueId: string }
	| { type: "UNSKIP_ISSUE"; issueId: string }
	| { type: "SKIP_ALL_STEP_ISSUES" }
	| { type: "NEXT_STEP" }
	| { type: "PREV_STEP" }
	| { type: "NEXT_ISSUE" }
	| { type: "PREV_ISSUE" }
	| { type: "GO_TO_STEP"; step: WizardStep }
	| { type: "GO_TO_ISSUE"; index: number }
	| { type: "SET_ISSUES"; issues: WizardIssues; preserveStep?: boolean }
	| { type: "SET_VALIDATING"; isValidating: boolean }
	| { type: "SET_VALIDATION_ERROR"; error: string | null }
	| { type: "MARK_STEP_COMPLETE"; step: WizardStep }
	| { type: "RESET" };

// ============================================================================
// Component Props Types
// ============================================================================

/**
 * Initial geodata for configuration phase (alternative to initialJobs)
 */
export interface InitialGeoData {
	/** File validation warnings from parsing */
	warnings: FileValidationWarning[];
	/** List of uploaded files */
	uploadedFiles: UploadedFileInfo[];
	/** Original GeoJSON data map from file parsing */
	geoDataMap: GeoJSONDataMap;
	/** Selectable geodata (categorized by geo-type) */
	selectableGeoData: GroupedGeoJSONFiles;
	/** Original files that were uploaded */
	originalFiles: File[];
}

export interface WizardContainerProps {
	/** Initial geodata for configuration phase (starts at file-validation step) */
	initialGeoData?: InitialGeoData;
	/** Initial jobs to validate and fix (skips configuration, starts at validation phase) */
	initialJobs?: Job[];
	/** Called when wizard completes successfully with fixed jobs */
	onComplete: (jobs: Job[]) => void;
	/** Called when wizard is cancelled */
	onCancel: () => void;
	/** Company configuration (for field visibility/required settings) */
	company?: Company;
	/** All available deals for dropdown */
	allDeals?: Deal[];
	/** Available salespersons for dropdown */
	salespersons?: Contact[];
	/** Available branches for dropdown */
	branches?: Branch[];
	/** Pre-selected branch from parent form (applied to created jobs) */
	selectedBranch?: Branch;
	/** Available growers for autocomplete */
	growers?: Grower[];
	/** Current user (for visibility rules) */
	user?: AuthUser;
	/** Last crop options for autocomplete */
	lastCropOptions?: string[];
	/** Next crop options for autocomplete */
	nextCropOptions?: string[];
	/** When true, renders content without Dialog wrapper (for embedding in parent modal) */
	renderWithoutDialog?: boolean;
	/** Called when user clicks Start Over button (only shown when provided) */
	onStartOver?: () => void;
}

export interface StepProgressProps {
	currentStep: WizardStep;
	completedSteps: Set<WizardStep>;
	skippedSteps: Set<WizardStep>;
	issues: WizardIssues;
	/** Current wizard phase */
	phase: WizardPhase;
	/** Configuration state (for showing configuration steps) */
	configuration?: WizardConfigurationState;
	onStepClick?: (step: WizardStep) => void;
}

export interface WizardNavigationProps {
	canProceed: boolean;
	isFirstStep: boolean;
	isLastStep: boolean;
	currentIssueIndex: number;
	totalIssues: number;
	disabledReason?: string;
	onPrevious: () => void;
	onNext: () => void;
	onPreviousIssue?: () => void;
	onNextIssue?: () => void;
	onFinish?: () => void;
}

export interface IssueCardProps {
	jobs: Job[];
	issueType: "overlap" | "duplicate" | "unmatched" | "invalid";
	issueDescription: string;
	filePaths?: string[];
	onDeleteJobs?: (jobIds: string[]) => void;
	onDeleteJob?: (jobId: string) => void;
}

export interface IssueMapProps {
	jobs: Job[];
	visualizationMode: "boundaries" | "duplicate-features" | "unmatched";
	highlightGeometry?: Feature<Geometry>;
	nearbyJobs?: NearbyJobInfo[];
	/** Height of the map container */
	height?: string;
}

export interface IssuePaginationProps {
	currentIndex: number;
	totalCount: number;
	issueType: string;
	onPrevious: () => void;
	onNext: () => void;
	onGoTo: (index: number) => void;
}

// ============================================================================
// Helper Types
// ============================================================================

/**
 * Result of step validation check
 */
export interface StepValidationResult {
	isValid: boolean;
	errorMessage?: string;
}

/**
 * Configuration for each wizard step
 */
export interface StepConfig {
	step: WizardStep;
	title: string;
	description: string;
	/** Returns issues for this step */
	getIssues: (state: WizardState) => (OverlapIssue | DuplicateIssue | UnmatchedIssue | InvalidDataIssue)[];
	/** Checks if step should be shown (has issues) */
	hasIssues: (state: WizardState) => boolean;
	/** Checks if all issues in step are resolved */
	isComplete: (state: WizardState) => boolean;
}

/**
 * Form context props passed from parent for JobDetailModal integration
 */
export interface FormContextProps {
	company?: Company;
	allDeals?: Deal[];
	salespersons?: Contact[];
	branches?: Branch[];
	growers?: Grower[];
	user?: AuthUser;
	lastCropOptions?: string[];
	nextCropOptions?: string[];
}

/**
 * Context value for wizard state sharing
 */
export interface WizardContextValue {
	state: WizardState;
	dispatch: (action: WizardAction) => void;
	stepConfigs: StepConfig[];
	currentStepConfig: StepConfig | null;
	canProceedToNextStep: boolean;
	getActiveIssuesForCurrentStep: () => (OverlapIssue | DuplicateIssue | UnmatchedIssue | InvalidDataIssue)[];
	/** Form context for JobDetailModal integration */
	formContext: FormContextProps;
}
